import React, {useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {Link} from 'react-router-dom';
import styles from './FooterSEOLinks.scss';
import PropTypes from 'prop-types';
import {getSurroundingSuburbs} from '../../actions';
import {toTitleCase} from '../../lib/formatting';

const propertyTypeLinks = [
  {label: 'Houses', query: 'House'},
  {label: 'Apartments', query: 'Apartment'}
];

const sellYourPropertyLinks = ['agents', 'agencies'];

const FooterSEOLinks = (props) =>  {
  const surroundingSuburbs = useSelector(state => state.surroundingSuburbs)
  const dispatch = useDispatch();
  const { suburb = '', state = '', postCode = '', type} = props;
  let suburbCode = suburb.replace(' ', '-');

  useEffect(() => {
    if (isAddressComplete(suburb, state, postCode)) {
      const sanitizedSuburb = suburb.trim().toUpperCase().replace(/\s/, '_');
      const sanitizedState = state.trim().toUpperCase();
      const addressId =  sanitizedSuburb + '+' + sanitizedState + '+' + postCode.trim();

      if (!surroundingSuburbs || surroundingSuburbs?.status !== 'success') dispatch(getSurroundingSuburbs(addressId));
      if (surroundingSuburbs && surroundingSuburbs?.request !== addressId) dispatch(getSurroundingSuburbs(addressId));
    }
  }, [suburb]);

  const isAddressComplete = (suburb, state, postCode) => {
    return suburb.trim().length > 0 && state.trim().length > 0 && postCode.trim().length > 0;
  };

  const getForSalePropertyInSuburbUrl = (state, suburbCode, postCode) => {
    return `/property-for-sale/${state.toLowerCase()}/${suburbCode.toLowerCase()}-${postCode}`;
  };

  const getForRentPropertyInSuburbUrl = (state, suburbCode, postCode) => {
    return `/property-for-rent/${state.toLowerCase()}/${suburbCode.toLowerCase()}-${postCode}`;
  };

  const getRecentSoldPropertyInSuburbUrl = (state, suburbCode, postCode) => {
    return `/sold/${state.toLowerCase()}/${suburbCode.toLowerCase()}-${postCode}`;
  };

  const getOffMarketPropertyInSuburbUrl = (state, suburbCode, postCode) => {
    return `/property/${state.toLowerCase()}/${suburbCode.toLowerCase()}-${postCode}`;
  };

  const getAgentsInSuburbUrl = (state, suburbCode, postCode) => {
    return `/real-estate-agents/${state.toLowerCase()}/${suburbCode.toLowerCase()}-${postCode}`;
  };

  const getAgenciesInSuburbUrl = (state, suburbCode, postCode) => {
    return `/real-estate-agencies/${state.toLowerCase()}/${suburbCode.toLowerCase()}-${postCode}`;
  };

  const getSuburbProfileUrl = (state, suburbCode, postCode) => {
    return `/suburb/${state.toLowerCase()}/${suburbCode.toLowerCase()}-${postCode}`;
  };

  const getSuburbProfileFooter = () => {
    return (
      <div className={styles.seoLinksContainerWrapper + (props.isWrap ? ` ${styles.wrapContainer}` : '')}>
        <div className={styles.seoLinksContainer}>
          <p><strong>Real estate to buy in surrounding areas</strong></p>
          <Link to={getForSalePropertyInSuburbUrl(state, suburbCode, postCode)} className={styles.seoLink}>
            Properties for sale in {toTitleCase(suburb)}
          </Link>
          { surroundingSuburbs?.status === 'success' && !!surroundingSuburbs?.surroundingSuburbs?.length ?
            surroundingSuburbs.surroundingSuburbs.slice(0,4).map((item, itemIndex) =>
              <div key={itemIndex}>
                <Link to={getForSalePropertyInSuburbUrl(item.stateCode, item.suburb.replace(' ', '-'), item.postCode)}
                  className={styles.seoLink} >
                  Properties for sale in {toTitleCase(item.suburb)}, {item.postCode}
                </Link>
                <span data="seoData">&nbsp;</span>
              </div>
            ) : null
          }
        </div>
        <div className={styles.seoLinksContainer}>
          <p><strong>Real estate to rent in surrounding areas</strong></p>
          <Link to={getForRentPropertyInSuburbUrl(state, suburbCode, postCode)} className={styles.seoLink}>
            Properties for rent in {toTitleCase(suburb)}
          </Link><span data="seoData">&nbsp;</span>
          { surroundingSuburbs?.status === 'success' && !!surroundingSuburbs?.surroundingSuburbs?.length ?
            surroundingSuburbs.surroundingSuburbs.slice(0,4).map((item, itemIndex) =>
              <div key={itemIndex}>
                <Link to={getForRentPropertyInSuburbUrl(item.stateCode, item.suburb.replace(' ', '-'), item.postCode)}
                  className={styles.seoLink}>
                  Properties for rent in {toTitleCase(item.suburb)}, {item.postCode}
                </Link>
                <span data="seoData">&nbsp;</span>
              </div>
            ) : null
          }
        </div>
        <div className={styles.seoLinksContainer}>
          <p><strong>Real estate data</strong></p>
          <Link to={getRecentSoldPropertyInSuburbUrl(state, suburbCode, postCode)} className={styles.seoLink}>
            Recent property sales in {toTitleCase(suburb)}
          </Link><span data="seoData">&nbsp;<br/></span>
          <Link to={getOffMarketPropertyInSuburbUrl(state, suburbCode, postCode)} className={styles.seoLink}>
            Estimated property values in {toTitleCase(suburb)}
          </Link>
        </div>
        <div className={styles.seoLinksContainer}>
          <p><strong>Find an agent</strong></p>
          <Link to={getAgentsInSuburbUrl(state, suburbCode, postCode)} className={styles.seoLink}>
            Compare real estate agents in {toTitleCase(suburb)}
          </Link><span data="seoData">&nbsp;<br/></span>
          <Link to={getAgenciesInSuburbUrl(state, suburbCode, postCode)} className={styles.seoLink}>
            Compare real estate agencies in {toTitleCase(suburb)}
          </Link>
        </div>
        { surroundingSuburbs?.status === 'success' && !!surroundingSuburbs?.surroundingSuburbs?.length &&
            <div className={styles.seoLinksContainer}>
              <p><strong>Local market investment data</strong></p>
              {surroundingSuburbs.surroundingSuburbs.map((item, itemIndex) =>
                <div key={itemIndex}>
                  <Link to={getSuburbProfileUrl(item.stateCode, item.suburb.replace(' ', '-'), item.postCode)}
                    className={styles.seoLink} >
                      Suburb Profile for {toTitleCase(item.suburb)}, {item.postCode}
                  </Link>
                  <span data="seoData">&nbsp;</span>
                </div>
              )}
            </div>}
      </div>
    );
  };

  const getDefaultFooter = () => {
    return (
      <div className={styles.seoLinksContainerWrapper + (props.isWrap ? ` ${styles.wrapContainer}` : '')}>
        <div className={styles.seoLinksContainer}>
          <p><strong>Real estate to buy</strong></p>
          {propertyTypeLinks.map((item, itemIndex) =>
            <div key={itemIndex}>
              <Link to={`${getForSalePropertyInSuburbUrl(state, suburbCode, postCode)}?types=${item.query}`}
                className={styles.seoLink}
              >
                {item.label} for sale in {toTitleCase(suburb)}
              </Link>
              <span data="seoData">&nbsp;</span>
            </div>
          )}
        </div>
        <div className={styles.seoLinksContainer}>
          <p><strong>Real estate to rent</strong></p>
          {propertyTypeLinks.map((item, itemIndex) =>
            <div key={itemIndex}>
              <Link to={`${getForRentPropertyInSuburbUrl(state, suburbCode, postCode)}?types=${item.query}`}
                className={styles.seoLink}
              >
                {item.label} for rent in {toTitleCase(suburb)}
              </Link>
              <span data="seoData">&nbsp;</span>
            </div>
          )}
        </div>
        <div className={styles.seoLinksContainer}>
          <p><strong>Property research</strong></p>
          {propertyTypeLinks.map((item, itemIndex) =>
            <div key={itemIndex}>
              <Link to={`${getRecentSoldPropertyInSuburbUrl(state, suburbCode, postCode)}?types=${item.query}`}
                className={styles.seoLink}
              >
                Recent {item.query.toLowerCase()} sales in {toTitleCase(suburb)}
              </Link>
              <span data="seoData">&nbsp;</span>
            </div>
          )}
          {propertyTypeLinks.map((item, itemIndex) =>
            <div key={itemIndex}>
              <Link to={`${getOffMarketPropertyInSuburbUrl(state, suburbCode, postCode)}?types=${item.query}`}
                className={styles.seoLink}
              >
                {item.query} values in {toTitleCase(suburb)}
              </Link>
              <span data="seoData">&nbsp;</span>
            </div>
          )}
        </div>
        <div className={styles.seoLinksContainer}>
          <p><strong>Selling your property</strong></p>
          {sellYourPropertyLinks.map((item, itemIndex) =>
            <div key={itemIndex}>
              <Link to={`/real-estate-${item}/${state.toLowerCase()}/${suburbCode.toLowerCase()}-${postCode}`}
                className={styles.seoLink}
              >
                Find real estate {item} in {toTitleCase(suburb)}
              </Link>
              <span data="seoData">&nbsp;</span>
            </div>
          )}
        </div>
        { surroundingSuburbs?.status === 'success' && !!surroundingSuburbs?.surroundingSuburbs?.length &&
              <div className={styles.seoLinksContainer}>
                <p><strong>Explore surrounding areas</strong></p>
                {surroundingSuburbs.surroundingSuburbs.map((item, itemIndex) =>
                  <div key={itemIndex}>
                    <Link to={`${getForSalePropertyInSuburbUrl(item.stateCode, item.suburb.replace(' ', '-'), item.postCode)}`}
                      className={styles.seoLink}
                    >
                        Real estate in {toTitleCase(item.suburb)}, {item.postCode}
                    </Link>
                    <span data="seoData">&nbsp;</span>
                  </div>
                )}
              </div>
        }
      </div>
    );
  };

  const footer = {
    suburbProfile: getSuburbProfileFooter(),
    default: getDefaultFooter(),
  };

  return <div>
    {isAddressComplete(suburb, state, postCode) ? footer[type ? type : 'default'] : null}
  </div>;
};

FooterSEOLinks.propTypes = {
  suburb: PropTypes.string,
  state: PropTypes.string,
  postCode: PropTypes.string,
  isWrap: PropTypes.bool,
  type: PropTypes.string,
};

export default FooterSEOLinks;
